import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Star, Truck, PackageCheck, AlertTriangle, Clock, Package } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, formatPrice, formatDate, errText, ORDER_STATUS, PAYOUT_STATUS } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

function ShipDialog({ order, onDone }) {
  const [carrier, setCarrier] = useState("DHL");
  const [tracking, setTracking] = useState("");
  const submit = async () => {
    try {
      await api.post(`/orders/${order.id}/ship`, { carrier, tracking_number: tracking });
      toast.success("Versand bestätigt");
      onDone();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid={`ship-btn-${order.id}`} className="bg-primary text-primary-foreground text-xs font-bold px-4 py-2 rounded-full hover:opacity-90 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Versenden</button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Versand bestätigen</DialogTitle></DialogHeader>
        <Select value={carrier} onValueChange={setCarrier}>
          <SelectTrigger data-testid="ship-carrier-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["DHL", "Hermes", "DPD", "GLS", "UPS", "Deutsche Post"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <input data-testid="ship-tracking-input" className="w-full rounded-xl border border-border px-4 py-3 text-sm" placeholder="Sendungsnummer" value={tracking} onChange={(e) => setTracking(e.target.value)} />
        <button data-testid="ship-submit-btn" onClick={submit} disabled={tracking.length < 3} className="bg-primary text-primary-foreground font-bold py-3 rounded-full disabled:opacity-50">Versand bestätigen</button>
      </DialogContent>
    </Dialog>
  );
}

function DisputeDialog({ order, onDone }) {
  const [reason, setReason] = useState("NICHT_ANGEKOMMEN");
  const [details, setDetails] = useState("");
  const submit = async () => {
    try {
      await api.post(`/orders/${order.id}/dispute`, { reason, details });
      toast.success("Problem gemeldet — unser Team prüft den Fall");
      onDone();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid={`dispute-btn-${order.id}`} className="border border-destructive text-destructive text-xs font-bold px-4 py-2 rounded-full hover:bg-destructive/10 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Problem melden</button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Problem melden</DialogTitle></DialogHeader>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger data-testid="dispute-reason-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="NICHT_ANGEKOMMEN">Artikel nicht angekommen</SelectItem>
            <SelectItem value="FALSCHER_ARTIKEL">Falscher Artikel</SelectItem>
            <SelectItem value="BESCHAEDIGT">Beschädigt</SelectItem>
            <SelectItem value="ANDERS_ALS_BESCHRIEBEN">Erheblich anders als beschrieben</SelectItem>
            <SelectItem value="NICHT_VERSENDET">Verkäufer hat nicht versendet</SelectItem>
          </SelectContent>
        </Select>
        <textarea data-testid="dispute-details-input" className="w-full rounded-xl border border-border p-3 text-sm min-h-24" placeholder="Beschreibe das Problem…" value={details} onChange={(e) => setDetails(e.target.value)} />
        <button data-testid="dispute-submit-btn" onClick={submit} className="bg-destructive text-white font-bold py-3 rounded-full">Fall eröffnen</button>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ order, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post("/reviews", { order_id: order.id, rating, comment });
      toast.success("Danke für deine Bewertung!");
      onDone();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid={`review-btn-${order.id}`} className="border border-primary text-primary text-xs font-bold px-4 py-2 rounded-full hover:bg-secondary flex items-center gap-1.5"><Star className="w-3.5 h-3.5" /> Bewerten</button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Bewertung abgeben</DialogTitle></DialogHeader>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} data-testid={`star-${s}`} onClick={() => setRating(s)}>
              <Star className={`w-7 h-7 ${s <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
            </button>
          ))}
        </div>
        <textarea data-testid="review-comment-input" className="w-full rounded-xl border border-border p-3 text-sm min-h-20" placeholder="Kommentar (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
        <button data-testid="review-submit-btn" onClick={submit} className="bg-primary text-primary-foreground font-bold py-3 rounded-full">Bewertung senden</button>
      </DialogContent>
    </Dialog>
  );
}

function DeadlineChip({ o, seller }) {
  if (o.status !== "PAID" || !o.ship_deadline_at) return null;
  const msLeft = new Date(o.ship_deadline_at) - Date.now();
  const daysLeft = Math.ceil(msLeft / 86400000);
  const overdue = msLeft < 0;
  const date = new Date(o.ship_deadline_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  return (
    <span data-testid={`ship-deadline-${o.id}`}
      className={`text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1 ${overdue ? "bg-red-100 text-red-700" : daysLeft <= 1 ? "bg-amber-100 text-amber-700" : "bg-secondary text-foreground/80"}`}>
      <Clock className="w-3 h-3" />
      {overdue
        ? (seller ? "Versandfrist überschritten!" : "Versand überfällig")
        : (seller ? `Versenden bis ${date} (${daysLeft} ${daysLeft === 1 ? "Tag" : "Tage"})` : `Versand bis ${date}`)}
    </span>
  );
}

function PackstationInfo({ o }) {
  const a = o.shipping_address;
  if (!a || a.type !== "packstation") return null;
  return (
    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1" data-testid={`packstation-info-${o.id}`}>
      <Package className="w-3 h-3" /> DHL Packstation {a.station_number}, Postnr. {a.post_number}, {a.zip} {a.city}
    </p>
  );
}

export default function Orders() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "verkaeufe" ? "sales" : "purchases";
  const [orders, setOrders] = useState(null);

  const load = useCallback(() => {
    api.get("/orders", { params: { role: tab === "sales" ? "seller" : "buyer" } })
      .then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const confirmDelivery = async (o) => {
    try {
      await api.post(`/orders/${o.id}/confirm-delivery`);
      toast.success("Zustellung bestätigt — Käuferschutzfrist läuft");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-2xl sm:text-4xl font-bold mb-6">Bestellungen</h1>
      <Tabs value={tab} onValueChange={(v) => setParams(v === "sales" ? { tab: "verkaeufe" } : {})}>
        <TabsList className="rounded-full">
          <TabsTrigger data-testid="tab-purchases" value="purchases" className="rounded-full">Käufe</TabsTrigger>
          <TabsTrigger data-testid="tab-sales" value="sales" className="rounded-full">Verkäufe</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-6 space-y-4">
        {orders === null && [...Array(3)].map((_, i) => <div key={i} className="h-28 bg-muted rounded-2xl animate-pulse" />)}
        {orders?.length === 0 && <p className="text-muted-foreground text-sm py-10 text-center" data-testid="orders-empty">Noch keine {tab === "sales" ? "Verkäufe" : "Käufe"}.</p>}
        {orders?.map((o) => (
          <div key={o.id} data-testid={`order-card-${o.id}`} className="border border-border rounded-2xl p-4 flex flex-wrap gap-4 items-center">
            <Link to={`/artikel/${o.item_id}`}>
              {o.item_image_ids?.[0] ? <img src={imgUrl(o.item_image_ids[0], "thumb")} alt="" className="w-16 h-16 rounded-xl object-cover" /> : <div className="w-16 h-16 rounded-xl bg-muted" />}
            </Link>
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold text-sm">{o.item_title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{formatDate(o.created_at)} · {formatPrice(o.total_amount)}</p>
              <div className="flex flex-wrap gap-2 mt-1.5">
                <span className="text-xs bg-secondary font-semibold px-2.5 py-1 rounded-md" data-testid={`order-status-${o.id}`}>{ORDER_STATUS[o.status] || o.status}</span>
                <DeadlineChip o={o} seller={tab === "sales"} />
                {tab === "sales" && <span className="text-xs bg-muted font-semibold px-2.5 py-1 rounded-md">Auszahlung: {PAYOUT_STATUS[o.payout_status] || o.payout_status}</span>}
                {o.shipping?.tracking_number && <span className="text-xs text-muted-foreground py-1">{o.shipping.carrier}: {o.shipping.tracking_number}</span>}
              </div>
              {tab === "sales" && <PackstationInfo o={o} />}
            </div>
            <div className="flex flex-wrap gap-2">
              {tab === "sales" && o.status === "PAID" && <ShipDialog order={o} onDone={load} />}
              {tab === "purchases" && o.status === "SHIPPED" && (
                <button data-testid={`confirm-delivery-btn-${o.id}`} onClick={() => confirmDelivery(o)}
                  className="bg-primary text-primary-foreground text-xs font-bold px-4 py-2 rounded-full hover:opacity-90 flex items-center gap-1.5"><PackageCheck className="w-3.5 h-3.5" /> Erhalt bestätigen</button>
              )}
              {tab === "purchases" && ["PAID", "SHIPPED", "DELIVERED"].includes(o.status) && !["REFUNDED", "PAYOUT_COMPLETED"].includes(o.payout_status) && <DisputeDialog order={o} onDone={load} />}
              {["DELIVERED", "COMPLETED"].includes(o.status) && <ReviewDialog order={o} onDone={load} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
